import type {
  EgressProfileRecord,
  KeyChannelBinding,
  UpstreamAccountRecord,
  UpstreamChannelRecord,
  UpstreamKeyBucketRecord,
  UpstreamModelCapabilityRecord,
} from '@nadoutong/contracts';
import { groupCapabilitiesByNamespace } from '../upstream-model-skus/modelSkuGrouping.ts';

/**
 * Supply-access readiness — the derivation behind section 1 of the upstream
 * detail page (epic #1327 PR-6).
 *
 * The section answers one question per upstream namespace: can this namespace
 * carry traffic at all? Four facts, in the order an operator satisfies them:
 *
 *   1. `account` — the upstream has an enabled supplier account. Accounts
 *      carry no channel column, so this is deliberately upstream-scoped and
 *      reads the same on every row: it separates "no accounts exist" from
 *      "accounts exist but none reach this namespace", which is the next fact.
 *   2. `key`     — an enabled key bucket owned by an enabled account is bound
 *      to this namespace. This is the join that makes an account reachable
 *      from a namespace.
 *   3. `egress`  — every one of those bindings resolves to a usable transport.
 *   4. `probe`   — the namespace has at least one live model classified with a
 *      probe protocol (or already passed a probe), so probing can run at all.
 *
 * Each fact is three-state. "We could not read this" is not "this is broken":
 * a failed or in-flight query reports `unknown`, never `blocked`, so the
 * section cannot invent a blocker the server never reported.
 */

export type SupplyAccessFactId = 'account' | 'key' | 'egress' | 'probe';

export type SupplyAccessState = 'ready' | 'blocked' | 'unknown';

/** Fixed render order — the order an operator satisfies the chain. */
export const SUPPLY_ACCESS_FACT_IDS: readonly SupplyAccessFactId[] = [
  'account',
  'key',
  'egress',
  'probe',
] as const;

export interface SupplyAccessFact {
  readonly id: SupplyAccessFactId;
  readonly state: SupplyAccessState;
}

export interface SupplyAccessRow {
  readonly channelId: string;
  readonly channelSlug: string;
  readonly channelEnabled: boolean;
  /** The namespace survives only in capability evidence; its channel row is gone. */
  readonly stale: boolean;
  /** Always one entry per `SUPPLY_ACCESS_FACT_IDS`, in that order. */
  readonly facts: readonly SupplyAccessFact[];
}

/**
 * Every input a fact depends on, with `undefined` meaning "not readable"
 * (in flight or failed). Channels are required because they are the rows
 * themselves — an unresolved channel list has nothing to render, which the
 * caller handles as a section-level state rather than a row-level one.
 */
export interface SupplyAccessInputs {
  readonly channels: readonly UpstreamChannelRecord[];
  readonly capabilities: readonly UpstreamModelCapabilityRecord[] | undefined;
  readonly accounts: readonly UpstreamAccountRecord[] | undefined;
  readonly keyBuckets: readonly UpstreamKeyBucketRecord[] | undefined;
  /**
   * Bindings per key bucket. `undefined` when any single bucket's bindings
   * failed to load: a partial index would silently under-report the bindings
   * a namespace actually has, which reads as a blocker that does not exist.
   */
  readonly bindingsByBucketId: ReadonlyMap<string, readonly KeyChannelBinding[]> | undefined;
  readonly egressProfiles: readonly EgressProfileRecord[] | undefined;
}

function state(ready: boolean): SupplyAccessState {
  return ready ? 'ready' : 'blocked';
}

/**
 * A capability an operator can still act on. Ignored rows are disabled on
 * purpose and rows missing from the upstream's own list no longer exist
 * there, so neither can be classified or probed into readiness.
 */
function isLive(capability: UpstreamModelCapabilityRecord): boolean {
  return capability.status !== 'ignored' && capability.status !== 'missing_from_list';
}

export function deriveSupplyAccessRows(inputs: SupplyAccessInputs): readonly SupplyAccessRow[] {
  const {
    channels,
    capabilities,
    accounts,
    keyBuckets,
    bindingsByBucketId,
    egressProfiles,
  } = inputs;

  // Sharing the grouping with section 2 is the point: both sections must
  // agree on what a namespace is, including stale ones, or an operator
  // reading readiness here would be looking at a different set of rows
  // than the table below it.
  const groups = groupCapabilitiesByNamespace(capabilities ?? [], channels);

  const enabledAccountIds = accounts === undefined
    ? undefined
    : new Set(accounts.filter((account) => account.enabled).map((account) => account.id));

  const accountState: SupplyAccessState = enabledAccountIds === undefined
    ? 'unknown'
    : state(enabledAccountIds.size > 0);

  const enabledProfileIds = egressProfiles === undefined
    ? undefined
    : new Set(egressProfiles.filter((profile) => profile.enabled).map((profile) => profile.id));

  /**
   * Bindings that can actually serve: the bucket is enabled and its owning
   * account is enabled. A key hanging off a disabled account is dead supply,
   * so counting it would report a namespace as reachable when it is not.
   */
  const servingBindings = keyBuckets === undefined ||
    bindingsByBucketId === undefined ||
    enabledAccountIds === undefined
    ? undefined
    : keyBuckets.flatMap((bucket) => {
      if (!bucket.enabled) return [];
      if (bucket.account_id === null || !enabledAccountIds.has(bucket.account_id)) return [];
      return [...(bindingsByBucketId.get(bucket.id) ?? [])];
    });

  return groups.map((group) => {
    const channelBindings = servingBindings?.filter(
      (binding) => binding.channel_id === group.channelId,
    );

    const keyState: SupplyAccessState = channelBindings === undefined
      ? 'unknown'
      : state(channelBindings.length > 0);

    // A binding naming no profile is not unconfigured — it is the protocol
    // default direct transport, which routes. Only a binding pointing at a
    // profile that is disabled or gone is broken, and it breaks silently,
    // which is exactly what this fact exists to surface. Requiring *every*
    // serving binding to resolve keeps one dead key from hiding behind a
    // healthy sibling.
    const egressState: SupplyAccessState = channelBindings === undefined ||
      enabledProfileIds === undefined
      ? 'unknown'
      : state(
        channelBindings.length > 0 &&
        channelBindings.every(
          (binding) => binding.egress_profile_id === null ||
            enabledProfileIds.has(binding.egress_profile_id),
        ),
      );

    // Per-model probe detail belongs to section 2; this fact is only the
    // coarse gate that section cannot show until it is satisfied. The
    // `passed` arm covers rows that probed green before classification was
    // recorded on the capability.
    const liveRows = group.rows.filter((row) => isLive(row.capability));
    const probeState: SupplyAccessState = capabilities === undefined
      ? 'unknown'
      : state(
        liveRows.some(
          (row) => row.capability.probe_protocol !== null || row.capability.status === 'passed',
        ),
      );

    const byId: Record<SupplyAccessFactId, SupplyAccessState> = {
      account: accountState,
      key: keyState,
      egress: egressState,
      probe: probeState,
    };

    return {
      channelId: group.channelId,
      channelSlug: group.channelSlug,
      channelEnabled: group.enabled,
      stale: group.stale,
      facts: SUPPLY_ACCESS_FACT_IDS.map((id) => ({ id, state: byId[id] })),
    };
  });
}
